import React, { useMemo } from 'react';
import {
  Box,
  CircularProgress,
  Paper,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Typography,
} from '@mui/material';
import { ResponsiveBar } from '@nivo/bar';
import { formatNumber } from '../../helpers/numberUtils';
import { clientLabel, renderInt } from './shared';

export interface OwnershipClientRow {
  client_code: string;
  owned: number;
  owned_main: number;
  owned_dup: number;
  owned_aa: number;
  suppressed: number;
}

export interface OwnershipResponse {
  date: string;
  multi_eligible_total: number;
  per_client: OwnershipClientRow[];
  by_eligible_count: Array<{ eligible_clients: number; users: number }>;
}

interface Props {
  data: OwnershipResponse | null;
  loading: boolean;
}

const tooltipBox = (label: string, value: string) => (
  <div
    style={{
      background: '#fff',
      color: '#111',
      padding: '6px 10px',
      borderRadius: 4,
      boxShadow: '0 2px 6px rgba(0,0,0,0.2)',
      fontSize: 13,
    }}
  >
    <strong>{label}</strong>: {value}
  </div>
);

const UnifiedOwnershipBlock: React.FC<Props> = ({ data, loading }) => {
  const barData = useMemo(
    () =>
      (data?.per_client ?? []).map((c) => ({
        client: clientLabel(c.client_code),
        Owned: Number(c.owned) || 0,
        Suppressed: Number(c.suppressed) || 0,
      })),
    [data],
  );

  if (loading && !data) return <CircularProgress sx={{ mb: 2 }} />;
  if (!data) return null;

  return (
    <Paper sx={{ backgroundColor: '#ffffff', p: 2, mb: 3, height: '100%' }}>
      <Typography variant="h6" sx={{ mb: 0.5 }}>
        Ownership ({data.date})
      </Typography>
      <Typography variant="caption" sx={{ color: '#555', display: 'block', mb: 1 }}>
        Nur Multi-Eligible-User ({renderInt(data.multi_eligible_total)}) — Single-List-User werden
        strukturell nie unterdrückt und stehen nicht im Ledger.
      </Typography>

      {barData.length === 0 ? (
        <Typography variant="body2" color="text.secondary">
          Keine Ownership-Daten für dieses Datum.
        </Typography>
      ) : (
        <>
          <Box height={220}>
            <ResponsiveBar
              data={barData}
              keys={['Owned', 'Suppressed']}
              indexBy="client"
              groupMode="stacked"
              margin={{ top: 10, right: 20, bottom: 30, left: 55 }}
              padding={0.3}
              colors={['#4caf50', '#ef9a9a']}
              theme={{
                text: { fill: '#111' },
                axis: { ticks: { text: { fill: '#111' } } },
                tooltip: { container: { background: '#ffffff', color: '#111' } },
              }}
              axisLeft={{ format: (v) => formatNumber(v as number, 0) }}
              labelSkipHeight={14}
              valueFormat={(v) => formatNumber(v as number, 0)}
              tooltip={({ id, value, indexValue }) =>
                tooltipBox(`${indexValue} · ${id}`, formatNumber(value as number, 0))
              }
            />
          </Box>

          <Box display="flex" gap={2} flexWrap="wrap" sx={{ mt: 1 }}>
            <TableContainer component={Paper} sx={{ backgroundColor: '#ffffff', flex: '1 1 220px' }}>
              <Table size="small">
                <TableHead>
                  <TableRow>
                    {['Client', 'Main', 'Dup', 'AA', 'Unterdrückt'].map((h) => (
                      <TableCell
                        key={h}
                        sx={{
                          backgroundColor: '#e0e0e0 !important',
                          color: 'black',
                          fontWeight: 'bold',
                        }}
                      >
                        {h}
                      </TableCell>
                    ))}
                  </TableRow>
                </TableHead>
                <TableBody>
                  {data.per_client.map((c) => (
                    <TableRow key={c.client_code}>
                      <TableCell>{clientLabel(c.client_code)}</TableCell>
                      <TableCell align="right">{renderInt(c.owned_main)}</TableCell>
                      <TableCell align="right">{renderInt(c.owned_dup)}</TableCell>
                      <TableCell align="right">{renderInt(c.owned_aa)}</TableCell>
                      <TableCell align="right">{renderInt(c.suppressed)}</TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            </TableContainer>

            <TableContainer component={Paper} sx={{ backgroundColor: '#ffffff', flex: '1 1 160px' }}>
              <Table size="small">
                <TableHead>
                  <TableRow>
                    {['# Listen', 'User'].map((h) => (
                      <TableCell
                        key={h}
                        sx={{
                          backgroundColor: '#e0e0e0 !important',
                          color: 'black',
                          fontWeight: 'bold',
                        }}
                      >
                        {h}
                      </TableCell>
                    ))}
                  </TableRow>
                </TableHead>
                <TableBody>
                  {data.by_eligible_count.map((d) => (
                    <TableRow key={d.eligible_clients}>
                      <TableCell>{d.eligible_clients}</TableCell>
                      <TableCell align="right">{renderInt(d.users)}</TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            </TableContainer>
          </Box>
        </>
      )}
    </Paper>
  );
};

export default UnifiedOwnershipBlock;
